import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

/* ── 3D Tilt Card ──────────────────────────────────────── */
const TiltCard = ({ children, delay = 0, isDark }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    card.style.transform = `perspective(900px) rotateX(${-dy * 5}deg) rotateY(${dx * 5}deg) translateZ(6px)`;
    card.style.boxShadow = `0 30px 60px rgba(0,0,0,${isDark ? 0.4 : 0.15})`;
  };

  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
      cardRef.current.style.boxShadow = 'none';
    }
  };

  return (
    <motion.div
      ref={cardRef}
      style={{ transformStyle: 'preserve-3d', transition: 'transform 0.15s ease-out, box-shadow 0.3s ease', willChange: 'transform' }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </motion.div>
  );
};

/* ── Section ────────────────────────────────────────────── */
const ServicesSection = () => {
  const { servicesData, theme } = useLanguage();
  const isDark = theme === 'dark';
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useSpring(useTransform(scrollYProgress, [0, 1], ['-6%', '6%']), { stiffness: 50, damping: 18 });

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-24 md:py-36 relative overflow-hidden"
      style={{ background: isDark ? '#111' : '#F7F3EF' }}
    >
      {/* Parallax accent blob */}
      <motion.div
        style={{
          y: bgY, position: 'absolute', bottom: '-15%', left: '-6%',
          width: 500, height: 500,
          background: isDark
            ? 'radial-gradient(circle, rgba(184,134,11,0.08) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(184,134,11,0.06) 0%, transparent 70%)',
          filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0,
        }}
      />

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
            style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
          >
            <Sparkles size={14} style={{ color: 'var(--accent-gold-400)' }} />
            <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
              COMO POSSO AJUDÁ-LO(A)
            </span>
          </div>
          <h2 className="heading-hero mb-6">{servicesData.title}</h2>
          <p className="body-large mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
            {servicesData.subtitle}
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.services.map((service, i) => (
            <TiltCard key={service.id} delay={i * 0.1} isDark={isDark}>
              <div
                className="flex flex-col h-full p-8 rounded-2xl border transition-colors duration-500 relative overflow-hidden"
                style={{
                  background: isDark ? 'rgba(20,20,20,0.6)' : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(12px)',
                  borderColor: 'rgba(184,134,11,0.15)',
                  minHeight: 360,
                }}
              >
                {/* Top gold line accent — animates in */}
                <motion.div
                  style={{ position: 'absolute', top: 0, left: 0, height: 3, background: 'linear-gradient(90deg, var(--accent-gold-400), var(--accent-bronze-400))' }}
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.1 + 0.3, ease: [0.16, 1, 0.3, 1] }}
                />

                <div className="flex-1">
                  <h3 className="font-serif font-bold text-xl mb-3" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h3>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>

                  <ul className="space-y-3">
                    {service.features.map((feature, j) => (
                      <motion.li
                        key={j}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 + j * 0.07 + 0.4 }}
                      >
                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: 'rgba(184,134,11,0.15)', border: '1px solid rgba(184,134,11,0.3)' }}
                        >
                          <Check size={11} style={{ color: 'var(--accent-gold-400)' }} />
                        </div>
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/em-construcao"
                  className="mt-8 flex items-center gap-2 text-sm font-semibold group"
                  style={{ color: 'var(--accent-gold-400)', textDecoration: 'none' }}
                >
                  Saiba Mais
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
